import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";

import { AnnotationShapeType } from "../models/annotation-shape-type.enum";
import {
  Annotation,
  CreateAnnotationParams,
  UpdateAnnotationMessageParams,
  UpdateAnnotationShapeParams
} from "../models/annotation.model";

@Injectable({
  providedIn: "root"
})
export class AnnotationService {
  // Available colors for annotations
  private readonly ANNOTATION_COLORS = [
    "#FFFFFF", // White (default)
    "#000000", // Black
    "#FF5252", // Red
    "#448AFF", // Blue
    "#4CAF50", // Green
    "#FFC107", // Amber
    "#9C27B0", // Purple
    "#00BCD4", // Cyan
    "#FF9800" // Orange
  ];

  // Store annotations in a BehaviorSubject for reactive updates
  private _annotations = new BehaviorSubject<Annotation[]>([]);
  // Expose annotations as an Observable
  public readonly annotations$: Observable<Annotation[]> = this._annotations.asObservable();
  // Default to white
  private _defaultColorIndex = 0;

  constructor() {}

  // Track annotations that came from the saved revision
  private _savedAnnotationIds: string[] = [];

  // Expose saved annotation IDs
  public get savedAnnotationIds(): string[] {
    return this._savedAnnotationIds;
  }

  // Track annotations that came from URL
  private _urlAnnotationIds: string[] = [];

  // Expose URL annotation IDs
  public get urlAnnotationIds(): string[] {
    return this._urlAnnotationIds;
  }

  /**
   * Creates a new annotation from an existing one, preserving all properties
   * @param annotation The existing annotation to copy and update
   * @returns The newly created annotation
   */
  public createAnnotationFromExisting(annotation: Annotation): Annotation {
    // Add to the list of annotations
    const currentAnnotations = this._annotations.getValue();
    this._annotations.next([...currentAnnotations, annotation]);

    return annotation;
  }

  /**
   * Creates a new annotation
   * @param params The parameters for creating the annotation
   * @returns The created annotation
   */
  public createAnnotation(params: CreateAnnotationParams): Annotation {
    // Generate a hash-based ID using the annotation's properties
    const id = this._generateUniqueId({
      shapeType: params.shapeType,
      points: params.points || [],
      color: params.color || this.getDefaultColor(),
      opacity: params.opacity,
      title: params.title || "",
      message: params.message || "",
      linkUrl: params.linkUrl || ""
    });

    // Determine default opacity based on shape type
    let defaultOpacity = 0.8; // Default for rectangles and circles
    if (params.shapeType === AnnotationShapeType.TEXT) {
      defaultOpacity = 1.0; // Full opacity for text annotations
    }

    const annotation: Annotation = {
      id: id,
      shape: {
        type: params.shapeType,
        points: params.points || [],
        color: params.color || this.getDefaultColor(),
        opacity: params.opacity !== undefined ? params.opacity : defaultOpacity, // Type-specific default opacity
        lineWidth: params.lineWidth || 2
      },
      timestamp: Date.now()
    };

    // Add title and message if provided
    if (params.title) {
      annotation.title = params.title;
    }

    if (params.message) {
      annotation.message = params.message;
    }

    if (params.linkUrl) {
      annotation.linkUrl = params.linkUrl;
    }

    // Add to the list of annotations
    const currentAnnotations = this._annotations.getValue();
    this._annotations.next([...currentAnnotations, annotation]);

    return annotation;
  }

  /**
   * Creates an annotation from a pending shape and form data
   * Includes both the standard model properties and UI-specific display properties
   *
   * @param shapeData The pending shape data with UI-specific properties
   * @param formData The form data with title, message, and color
   * @returns The created annotation with both model and UI display properties
   */
  public createAnnotationFromPendingData(
    shapeData: {
      type: string;
      color?: string;
      cx?: number;
      cy?: number;
      r?: number;
      x?: number;
      y?: number;
      width?: number;
      height?: number;
    },
    formData: {
      title?: string;
      message?: string;
      color?: string;
      opacity?: number;
      linkUrl?: string;
    }
  ): Annotation {
    if (!shapeData) {
      throw new Error("No shape data provided");
    }

    // Determine shape type for the service model
    let shapeType: string;
    if (shapeData.type === "circle") {
      shapeType = "circle";
    } else if (shapeData.type === "text") {
      shapeType = "text";
    } else if (shapeData.type === "link") {
      shapeType = "link";
    } else {
      shapeType = "rectangle";
    }

    // Create points array based on shape type
    let points: Array<{ x: number; y: number }> = [];

    if (shapeData.type === "circle") {
      points = [
        { x: shapeData.cx, y: shapeData.cy }, // center
        { x: shapeData.cx + shapeData.r, y: shapeData.cy } // radius point
      ];
    } else if (shapeData.type === "rectangle" || shapeData.type === "text" || shapeData.type === "link") {
      points = [
        { x: shapeData.x, y: shapeData.y }, // top-left
        {
          x: shapeData.x + shapeData.width,
          y: shapeData.y + shapeData.height
        } // bottom-right
      ];
    }

    // Safely extract values with proper null handling
    const title = (formData?.title || "").trim();
    const message = (formData?.message || "").trim();
    const color = formData?.color || this.getDefaultColor();
    const linkUrl = (formData?.linkUrl || "").trim();

    // For text annotations, title is required
    if (shapeType === "text" && !title) {
      throw new Error("Title is required for text annotations");
    }

    // For link annotations, URL is required
    if (shapeType === "link" && !linkUrl) {
      throw new Error("URL is required for link annotations");
    }

    // Set different default opacity based on shape type
    let opacity;
    if (formData?.opacity !== undefined) {
      opacity = formData.opacity; // Use form data if provided
    } else if (shapeType === "text") {
      opacity = 1.0; // Full opacity for text by default
    } else {
      opacity = 0.8; // Default opacity for rectangles and circles
    }

    // Use the service method to create the annotation
    const newAnnotation = this.createAnnotation({
      shapeType: shapeType as any, // Cast to AnnotationShapeType
      points,
      color,
      opacity,
      title,
      message,
      linkUrl
    });

    // Add display-specific properties for UI rendering
    // These properties are needed for the UI to properly render the annotation
    if (shapeData.type === "circle") {
      (newAnnotation as any).cx = shapeData.cx;
      (newAnnotation as any).cy = shapeData.cy;
      (newAnnotation as any).r = shapeData.r;
      (newAnnotation as any).type = "circle";
      (newAnnotation as any).color = color; // Ensure color is available at top level for UI
      (newAnnotation as any).opacity = opacity; // Ensure opacity is available at top level for UI
      if (linkUrl) {
        (newAnnotation as any).linkUrl = linkUrl; // Add linkUrl to display properties
      }
    } else if (shapeData.type === "rectangle") {
      (newAnnotation as any).x = shapeData.x;
      (newAnnotation as any).y = shapeData.y;
      (newAnnotation as any).width = shapeData.width;
      (newAnnotation as any).height = shapeData.height;
      (newAnnotation as any).type = "rectangle";
      (newAnnotation as any).color = color; // Ensure color is available at top level for UI
      (newAnnotation as any).opacity = opacity; // Ensure opacity is available at top level for UI
      if (linkUrl) {
        (newAnnotation as any).linkUrl = linkUrl; // Add linkUrl to display properties
      }
    } else if (shapeData.type === "text") {
      (newAnnotation as any).x = shapeData.x;
      (newAnnotation as any).y = shapeData.y;
      (newAnnotation as any).width = shapeData.width;
      (newAnnotation as any).height = shapeData.height;
      (newAnnotation as any).type = "text";
      (newAnnotation as any).color = color; // Ensure color is available at top level for UI
      (newAnnotation as any).opacity = opacity; // Ensure opacity is available at top level for UI
    } else if (shapeData.type === "link") {
      (newAnnotation as any).x = shapeData.x;
      (newAnnotation as any).y = shapeData.y;
      (newAnnotation as any).width = shapeData.width;
      (newAnnotation as any).height = shapeData.height;
      (newAnnotation as any).type = "link";
      (newAnnotation as any).color = color; // Ensure color is available at top level for UI
      (newAnnotation as any).opacity = opacity; // Ensure opacity is available at top level for UI
    }

    return newAnnotation;
  }

  /**
   * Updates an annotation's shape
   * @param id The ID of the annotation
   * @param params The parameters to update
   */
  public updateAnnotationShape(id: string, params: UpdateAnnotationShapeParams): void {
    const annotations = this._annotations.getValue();
    const index = annotations.findIndex(a => a.id === id);

    if (index !== -1) {
      const oldAnnotation = annotations[index];
      const annotation = { ...oldAnnotation };

      // Store the original ID for reference
      (annotation as any).originalId = oldAnnotation.id;

      annotation.shape = {
        ...annotation.shape,
        ...(params.type !== undefined ? { type: params.type } : {}),
        ...(params.points !== undefined ? { points: params.points } : {}),
        ...(params.color !== undefined ? { color: params.color } : {}),
        ...(params.opacity !== undefined ? { opacity: params.opacity } : {}),
        ...(params.lineWidth !== undefined ? { lineWidth: params.lineWidth } : {})
      };

      // Generate a new ID based on updated properties
      annotation.id = this._generateUniqueId({
        shapeType: annotation.shape.type,
        points: annotation.shape.points,
        color: annotation.shape.color,
        opacity: annotation.shape.opacity,
        title: annotation.title || "",
        message: annotation.message || "",
        linkUrl: annotation.linkUrl || ""
      });

      // Update saved and URL annotations IDs arrays if needed
      this._updateAnnotationIdInArrays(oldAnnotation.id, annotation.id);

      // Update the annotation
      const updatedAnnotations = [...annotations];
      updatedAnnotations[index] = annotation;
      this._annotations.next(updatedAnnotations);
    }
  }

  /**
   * Updates an annotation's message details
   * @param id The ID of the annotation
   * @param params The parameters to update
   */
  public updateAnnotationMessage(id: string, params: UpdateAnnotationMessageParams): void {
    const annotations = this._annotations.getValue();
    const index = annotations.findIndex(a => a.id === id);

    if (index !== -1) {
      const oldAnnotation = annotations[index];
      const annotation = { ...oldAnnotation };

      // Store the original ID for reference
      (annotation as any).originalId = oldAnnotation.id;

      // Update title and message if provided
      if (params.title !== undefined) {
        annotation.title = params.title;
      }

      if (params.message !== undefined) {
        annotation.message = params.message;
      }

      if (params.linkUrl !== undefined) {
        annotation.linkUrl = params.linkUrl;
      }

      // Generate a new ID based on updated properties
      annotation.id = this._generateUniqueId({
        shapeType: annotation.shape.type,
        points: annotation.shape.points,
        color: annotation.shape.color,
        opacity: annotation.shape.opacity,
        title: annotation.title || "",
        message: annotation.message || "",
        linkUrl: annotation.linkUrl || ""
      });

      // Update saved and URL annotations IDs arrays if needed
      this._updateAnnotationIdInArrays(oldAnnotation.id, annotation.id);

      // Update the annotation
      const updatedAnnotations = [...annotations];
      updatedAnnotations[index] = annotation;
      this._annotations.next(updatedAnnotations);
    }
  }

  /**
   * Removes an annotation
   * @param id The ID of the annotation to remove
   */
  public removeAnnotation(id: string): void {
    const annotations = this._annotations.getValue();
    this._annotations.next(annotations.filter(a => a.id !== id));
  }

  /**
   * Clear all annotations
   */
  public clearAllAnnotations(): void {
    this._annotations.next([]);
    this._savedAnnotationIds = [];
    this._urlAnnotationIds = [];
  }

  /**
   * Get URL parameter representation of all annotations for sharing
   */
  public getUrlParam(): string {
    const annotations = this._annotations.getValue();
    if (!annotations || annotations.length === 0) {
      return "";
    }

    // Convert annotations to standard format if needed
    const standardizedAnnotations = annotations
      .map(a => this._convertToStandardFormat(a))
      .filter(a => a !== null) as Annotation[];

    if (standardizedAnnotations.length === 0) {
      console.error("Failed to convert annotations to standard format");
      return "";
    }

    // Convert to a compact format for URL
    const compactData = standardizedAnnotations.map(a => this._serializeAnnotation(a)).filter(a => a !== null); // Remove any null serialized annotations

    if (compactData.length === 0) {
      console.error("Failed to serialize any annotations");
      return "";
    }

    // Convert to Base64 for URL-safe encoding
    try {
      return btoa(JSON.stringify(compactData));
    } catch (error) {
      console.error("Error encoding annotations for URL", error);
      return "";
    }
  }

  /**
   * Load annotations from URL parameter
   * @param param The URL parameter containing encoded annotations
   */
  public loadFromUrlParam(param: string): void {
    try {
      // Decode Base64
      const jsonData = atob(param);

      // Parse JSON
      const compactData = JSON.parse(jsonData);

      // Convert compact format back to annotations
      const annotations = compactData.map(d => this._deserializeAnnotation(d));

      // Convert annotations to display format
      const displayReady = annotations.map(annotation => this._convertToDisplayFormat(annotation));

      // Store the IDs of annotations loaded from URL
      // Important: URL annotations IDs are tracked separately from saved annotation IDs
      // An annotation can be in both arrays if it was saved and also loaded from URL
      this._urlAnnotationIds = displayReady.map(annotation => annotation.id);

      // Set as current annotations
      this._annotations.next(displayReady);
    } catch (e) {
      console.error("Failed to parse annotation URL parameter", e);
      throw new Error("Invalid annotation data format");
    }
  }

  /**
   * Load annotations from a JSON string
   * @param jsonString The JSON string containing the annotations
   */
  public loadFromJsonString(jsonString: string): void {
    try {
      // Parse JSON string
      const annotationsData = JSON.parse(jsonString);

      // If it's already in the right format, use it directly
      if (Array.isArray(annotationsData)) {
        // Convert each annotation to display format if needed
        const displayReady = annotationsData
          .map(annotation => {
            // Check if it needs conversion to standard format first
            const standardFormat = this._convertToStandardFormat(annotation);

            if (standardFormat) {
              return this._convertToDisplayFormat(standardFormat);
            }
            // If already in display format, use as is
            return annotation;
          })
          .filter(ann => ann !== null && ann !== undefined);

        // Store IDs of annotations loaded from JSON (saved annotations)
        this._savedAnnotationIds = displayReady.map(annotation => annotation.id);

        // Set as current annotations
        this._annotations.next(displayReady);
      } else {
        console.error("Annotations data is not an array:", annotationsData);
        throw new Error("Annotations data is not an array");
      }
    } catch (e) {
      console.error("Failed to parse annotations JSON string", e);
      throw new Error("Invalid annotations data format");
    }
  }

  /**
   * Get all available annotation colors
   */
  public getColors(): string[] {
    return [...this.ANNOTATION_COLORS];
  }

  /**
   * Get the default color
   */
  public getDefaultColor(): string {
    return this.ANNOTATION_COLORS[this._defaultColorIndex];
  }

  /**
   * Helper method to handle annotation ID changes
   * @param oldId The old annotation ID
   * @param newId The new annotation ID
   */
  private _updateAnnotationIdInArrays(oldId: string, newId: string): void {
    // IMPORTANT: When an annotation is modified, it should no longer be considered
    // as coming from the saved annotations or from the URL.
    // We remove it from both tracking arrays instead of updating the ID.

    // Remove from saved annotations array if it was there
    const savedIndex = this._savedAnnotationIds.indexOf(oldId);
    if (savedIndex !== -1) {
      this._savedAnnotationIds.splice(savedIndex, 1);
    }

    // Remove from URL annotations array if it was there
    const urlIndex = this._urlAnnotationIds.indexOf(oldId);
    if (urlIndex !== -1) {
      this._urlAnnotationIds.splice(urlIndex, 1);
    }

    // The new ID is not added to either array, which means:
    // - It won't show the "saved" icon
    // - It won't show the "shared" icon
    // This is the desired behavior for modified annotations
  }

  /**
   * Convert legacy annotation format to the standardized Annotation interface
   */
  private _convertToStandardFormat(annotation: any): Annotation | null {
    try {
      if (!annotation) {
        console.error("Null or undefined annotation");
        return null;
      }

      if (!annotation.id) {
        console.error("Annotation missing ID property:", annotation);
        // Generate an ID since it's missing
        annotation.id = this._generateUniqueId();
      }

      // Extract title, message, and linkUrl from direct properties
      const title = annotation.title || "";
      const message = annotation.message || "";
      const linkUrl = annotation.linkUrl || "";

      // Handle circle format
      if (annotation.type === "circle") {
        if (annotation.cx === undefined || annotation.cy === undefined || annotation.r === undefined) {
          console.error("Circle annotation missing required properties:", annotation);
        }

        return {
          id: annotation.id,
          timestamp: Date.now(),
          shape: {
            type: AnnotationShapeType.CIRCLE,
            points: [
              { x: annotation.cx || 50, y: annotation.cy || 50 }, // Center point
              { x: (annotation.cx || 50) + (annotation.r || 10), y: annotation.cy || 50 } // Point to determine radius
            ],
            color: annotation.color || this.getDefaultColor(),
            opacity: annotation.opacity !== undefined ? annotation.opacity : 0.8,
            lineWidth: 2
          },
          title: title,
          message: message,
          ...(linkUrl ? { linkUrl } : {})
        };
      }

      // Handle rectangle format
      if (annotation.type === "rectangle") {
        if (
          annotation.x === undefined ||
          annotation.y === undefined ||
          annotation.width === undefined ||
          annotation.height === undefined
        ) {
          console.error("Rectangle annotation missing required properties:", annotation);
        }

        return {
          id: annotation.id,
          timestamp: Date.now(),
          shape: {
            type: AnnotationShapeType.RECTANGLE,
            points: [
              { x: annotation.x || 10, y: annotation.y || 10 }, // Top-left corner
              {
                x: (annotation.x || 10) + (annotation.width || 20),
                y: (annotation.y || 10) + (annotation.height || 20)
              } // Bottom-right corner
            ],
            color: annotation.color || this.getDefaultColor(),
            opacity: annotation.opacity !== undefined ? annotation.opacity : 0.8,
            lineWidth: 2
          },
          title: title,
          message: message,
          ...(linkUrl ? { linkUrl } : {})
        };
      }

      // Handle text format
      if (annotation.type === "text") {
        if (
          annotation.x === undefined ||
          annotation.y === undefined ||
          annotation.width === undefined ||
          annotation.height === undefined
        ) {
          console.error("Text annotation missing required properties:", annotation);
        }

        return {
          id: annotation.id,
          timestamp: Date.now(),
          shape: {
            type: AnnotationShapeType.TEXT,
            points: [
              { x: annotation.x || 10, y: annotation.y || 10 }, // Top-left corner
              {
                x: (annotation.x || 10) + (annotation.width || 20),
                y: (annotation.y || 10) + (annotation.height || 10)
              } // Bottom-right corner
            ],
            color: annotation.color || this.getDefaultColor(),
            opacity: annotation.opacity !== undefined ? annotation.opacity : 0.8,
            lineWidth: 2
          },
          title: title,
          message: message
        };
      }

      // Handle link format
      if (annotation.type === "link") {
        if (
          annotation.x === undefined ||
          annotation.y === undefined ||
          annotation.width === undefined ||
          annotation.height === undefined
        ) {
          console.error("Link annotation missing required properties:", annotation);
        }

        return {
          id: annotation.id,
          timestamp: Date.now(),
          shape: {
            type: AnnotationShapeType.LINK,
            points: [
              { x: annotation.x || 10, y: annotation.y || 10 }, // Top-left corner
              {
                x: (annotation.x || 10) + (annotation.width || 20),
                y: (annotation.y || 10) + (annotation.height || 10)
              } // Bottom-right corner
            ],
            color: annotation.color || this.getDefaultColor(),
            opacity: annotation.opacity !== undefined ? annotation.opacity : 0.8,
            lineWidth: 2
          },
          title: title,
          message: message,
          linkUrl: annotation.linkUrl || ""
        };
      }

      // If it already matches the standard format, return as is
      if (annotation.shape && annotation.shape.type && annotation.shape.points) {
        // Make a copy and ensure the color is set
        const standardAnnotation = { ...annotation };
        standardAnnotation.shape = {
          ...standardAnnotation.shape,
          color: standardAnnotation.shape.color || this.getDefaultColor()
        };
        return standardAnnotation;
      }

      console.warn("Unknown annotation format:", annotation);
      return null;
    } catch (error) {
      console.error("Error converting annotation format:", error);
      return null;
    }
  }

  /**
   * Convert an annotation from the internal format to the display format
   * with properties like x, y, width, height, cx, cy, r, etc.
   */
  private _convertToDisplayFormat(annotation: Annotation): any {
    try {
      const displayAnnotation: any = {
        id: annotation.id,
        timestamp: annotation.timestamp,
        color: annotation.shape.color,
        opacity: annotation.shape.opacity !== undefined ? annotation.shape.opacity : 0.8,
        title: annotation.title || "",
        message: annotation.message || ""
      };

      // Add linkUrl if it exists
      if (annotation.linkUrl) {
        displayAnnotation.linkUrl = annotation.linkUrl;
      }

      if (annotation.shape.type === AnnotationShapeType.RECTANGLE) {
        displayAnnotation.type = "rectangle";

        // Get the points that define the rectangle
        const p1 = annotation.shape.points[0] || { x: 0, y: 0 };
        const p2 = annotation.shape.points[1] || { x: 0, y: 0 };

        // Calculate rectangle properties
        const left = Math.min(p1.x, p2.x);
        const top = Math.min(p1.y, p2.y);
        const width = Math.abs(p2.x - p1.x);
        const height = Math.abs(p2.y - p1.y);

        // Assign to the display annotation
        displayAnnotation.x = left;
        displayAnnotation.y = top;
        displayAnnotation.width = width;
        displayAnnotation.height = height;
      } else if (annotation.shape.type === AnnotationShapeType.CIRCLE) {
        displayAnnotation.type = "circle";

        // Get the points that define the circle
        const center = annotation.shape.points[0] || { x: 50, y: 50 };
        const radiusPoint = annotation.shape.points[1] || { x: 60, y: 50 };

        // Calculate circle properties
        const dx = radiusPoint.x - center.x;
        const dy = radiusPoint.y - center.y;
        const radius = Math.sqrt(dx * dx + dy * dy);

        // Assign to the display annotation
        displayAnnotation.cx = center.x;
        displayAnnotation.cy = center.y;
        displayAnnotation.r = radius;
      } else if (annotation.shape.type === AnnotationShapeType.TEXT) {
        displayAnnotation.type = "text";

        // Get the points that define the text box
        const p1 = annotation.shape.points[0] || { x: 0, y: 0 };
        const p2 = annotation.shape.points[1] || { x: 0, y: 0 };

        // Calculate text box properties
        const left = Math.min(p1.x, p2.x);
        const top = Math.min(p1.y, p2.y);
        const width = Math.abs(p2.x - p1.x);
        const height = Math.abs(p2.y - p1.y);

        // Assign to the display annotation
        displayAnnotation.x = left;
        displayAnnotation.y = top;
        displayAnnotation.width = width;
        displayAnnotation.height = height;
      } else if (annotation.shape.type === AnnotationShapeType.LINK) {
        displayAnnotation.type = "link";

        // Get the points that define the link box
        const p1 = annotation.shape.points[0] || { x: 0, y: 0 };
        const p2 = annotation.shape.points[1] || { x: 0, y: 0 };

        // Calculate link box properties
        const left = Math.min(p1.x, p2.x);
        const top = Math.min(p1.y, p2.y);
        const width = Math.abs(p2.x - p1.x);
        const height = Math.abs(p2.y - p1.y);

        // Assign to the display annotation
        displayAnnotation.x = left;
        displayAnnotation.y = top;
        displayAnnotation.width = width;
        displayAnnotation.height = height;

        // Include the link URL
        if (annotation.linkUrl) {
          displayAnnotation.linkUrl = annotation.linkUrl;
        }
      }

      return displayAnnotation;
    } catch (error) {
      console.error("Error converting annotation to display format:", error, annotation);
      return {
        id: annotation.id || this._generateUniqueId(),
        type: "rectangle",
        x: 10,
        y: 10,
        width: 20,
        height: 20,
        color: this.getDefaultColor()
      };
    }
  }

  /**
   * Generate a unique ID for an annotation based on its properties
   * @returns A unique ID string
   */
  private _generateUniqueId(properties?: {
    shapeType?: string;
    points?: Array<{ x: number; y: number }>;
    color?: string;
    opacity?: number;
    title?: string;
    message?: string;
    linkUrl?: string;
  }): string {
    if (!properties) {
      // If no properties provided, use a timestamp-based ID
      return "ann_" + Date.now().toString() + "_" + Math.floor(Math.random() * 10000);
    }

    // Create a string representation of the annotation's key properties
    const shapeType = properties.shapeType || "";
    const points = properties.points ? JSON.stringify(properties.points) : "";
    const color = properties.color || "";
    const opacity = properties.opacity !== undefined ? properties.opacity.toString() : "0.8";
    const title = properties.title || "";
    const message = properties.message || "";
    const linkUrl = properties.linkUrl || "";

    // Combine all properties into a single string
    const propertyString = `${shapeType}|${points}|${color}|${opacity}|${title}|${message}|${linkUrl}`;

    // Create a simple hash of the property string
    // This implementation uses a basic hash function that produces a 32-bit integer hash
    let hash = 0;
    for (let i = 0; i < propertyString.length; i++) {
      const char = propertyString.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32bit integer
    }

    // Return a string ID with a prefix to make it recognizable
    return "ann_" + Math.abs(hash).toString(16);
  }

  /**
   * Serialize an annotation to a compact format for URL storage
   */
  private _serializeAnnotation(annotation: Annotation): any {
    // Validate annotation structure to avoid TypeError
    if (
      !annotation ||
      !annotation.shape ||
      !annotation.shape.type ||
      !annotation.shape.points ||
      !Array.isArray(annotation.shape.points)
    ) {
      console.error("Invalid annotation structure:", annotation);
      return null;
    }

    try {
      const compact: any = {
        i: annotation.id,
        t: annotation.timestamp,
        s: {
          t: this._serializeShapeType(annotation.shape.type),
          p: annotation.shape.points.map(p => [this._roundToTwo(p.x), this._roundToTwo(p.y)]),
          c: annotation.shape.color,
          o: this._roundToTwo(annotation.shape.opacity !== undefined ? annotation.shape.opacity : 0.8),
          w: annotation.shape.lineWidth
        }
      };

      // Add title and message if they exist
      if (annotation.title) {
        compact.title = annotation.title;
      }

      if (annotation.message) {
        compact.msg = annotation.message;
      }

      if (annotation.linkUrl) {
        compact.url = annotation.linkUrl;
      }

      return compact;
    } catch (error) {
      console.error("Error serializing annotation:", error, annotation);
      return null;
    }
  }

  /**
   * Deserialize a compact annotation from URL storage
   */
  private _deserializeAnnotation(data: any): Annotation {
    const annotation: Annotation = {
      id: data.i || this._generateUniqueId(),
      timestamp: data.t || Date.now(),
      shape: {
        type: this._deserializeShapeType(data.s.t),
        points: (data.s.p || []).map(p => ({ x: p[0], y: p[1] })),
        color: data.s.c || this.getDefaultColor(),
        opacity: data.s.o !== undefined ? data.s.o : 0.8,
        lineWidth: data.s.w || 2
      }
    };

    // Add title and message if they exist in the data
    if (data.title) {
      annotation.title = data.title;
    }

    if (data.msg) {
      annotation.message = data.msg;
    }

    if (data.url) {
      annotation.linkUrl = data.url;
    }

    return annotation;
  }

  /**
   * Serialize shape type to a compact format
   */
  private _serializeShapeType(type: AnnotationShapeType): number {
    switch (type) {
      case AnnotationShapeType.RECTANGLE:
        return 0;
      case AnnotationShapeType.CIRCLE:
        return 1;
      case AnnotationShapeType.TEXT:
        return 2;
      case AnnotationShapeType.LINK:
        return 3;
      default:
        return 0;
    }
  }

  /**
   * Deserialize shape type from a compact format
   */
  private _deserializeShapeType(type: number): AnnotationShapeType {
    switch (type) {
      case 0:
        return AnnotationShapeType.RECTANGLE;
      case 1:
        return AnnotationShapeType.CIRCLE;
      case 2:
        return AnnotationShapeType.TEXT;
      case 3:
        return AnnotationShapeType.LINK;
      default:
        return AnnotationShapeType.RECTANGLE;
    }
  }

  /**
   * Round a number to two decimal places for storage efficiency
   */
  private _roundToTwo(num: number): number {
    return Math.round((num + Number.EPSILON) * 100) / 100;
  }

  /**
   * Loads annotations from a revision object
   * Returns a promise that resolves when the loading is complete
   *
   * @param revision The image or revision object containing annotations
   * @returns Promise that resolves when loading is complete, or rejects with an error
   */
  public loadSavedAnnotations(revision: { annotations?: string }): Promise<void> {
    return new Promise((resolve, reject) => {
      // Check if we have annotations to load
      if (!revision || !revision.annotations || revision.annotations.trim() === "") {
        resolve();
        return;
      }

      try {
        // Load the annotations directly from the revision object
        // The annotations in the revision are already in JSON format
        this.loadFromJsonString(revision.annotations);

        // Return a promise that resolves after a small delay to ensure UI updates
        setTimeout(() => {
          resolve();
        }, 300);
      } catch (error) {
        console.error("Error loading annotations from revision:", error);
        this.clearAllAnnotations();
        reject(error);
      }
    });
  }
}
